import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts'
import ResearcherShell from '@/components/researcher/ResearcherShell'
import { adminService, type AdminMetrics } from '@/services/adminService'

/** How many rows to show in the per-account/per-study bar charts. */
const TOP_N = 12

/**
 * Make the x-axis labels of a chart row set unique.
 *
 * A stacked BarChart in recharts 3 keys its stacked dataset by x-axis category,
 * so two rows sharing a label collapse into one: the stacked data ends up
 * shorter than the data being drawn and rendering reads past its end and throws
 * (two researchers with the same display name were enough to blank this page).
 * Two bars with the same label are unreadable anyway — `suffix` supplies
 * something unique for the row (an email, an id) to tell the collisions apart.
 */
function withUniqueLabels<T extends { name: string }>(rows: T[], suffix: (row: T) => string): T[] {
  const seen = new Set<string>()
  return rows.map((row) => {
    const base = row.name || suffix(row)
    let name = base
    if (seen.has(name)) name = `${base} (${suffix(row)})`
    for (let n = 2; seen.has(name); n += 1) name = `${base} (${suffix(row)} ${n})`
    seen.add(name)
    return { ...row, name }
  })
}

function StatCard({
  label,
  value,
  sub,
  accent,
}: {
  label: string
  value: string | number
  sub?: string
  accent: string
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: '16px',
        border: `1px solid ${c.ink08}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        minWidth: 0,
      }}
    >
      <Box sx={{ width: 28, height: 4, borderRadius: 2, bgcolor: accent, mb: 1 }} />
      <Typography sx={{ fontSize: 13, color: c.ink56, fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: 30, fontWeight: 700, lineHeight: 1.1, color: c.ink }}>
        {value}
      </Typography>
      {sub && <Typography sx={{ fontSize: 12, color: c.ink48 }}>{sub}</Typography>}
    </Paper>
  )
}

function ChartCard({
  title,
  subtitle,
  height = 320,
  children,
}: {
  title: string
  subtitle?: string
  height?: number
  children: React.ReactElement
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  return (
    <Paper sx={{ p: 2.5, borderRadius: '16px', border: `1px solid ${c.ink08}` }}>
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: c.ink }}>{title}</Typography>
      {subtitle && (
        <Typography sx={{ fontSize: 13, color: c.ink56, mb: 1.5 }}>{subtitle}</Typography>
      )}
      <Box sx={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </Box>
    </Paper>
  )
}

export default function AdminDashboard() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const navigate = useNavigate()
  // undefined = loading, null = failed, value = loaded (house tri-state).
  const [metrics, setMetrics] = useState<AdminMetrics | null | undefined>(undefined)

  useEffect(() => {
    let cancelled = false
    adminService
      .getMetrics()
      .then((m) => {
        if (!cancelled) setMetrics(m)
      })
      .catch(() => {
        if (!cancelled) setMetrics(null)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Derived chart datasets (memoized so recharts doesn't re-diff every render).
  const growth = useMemo(() => {
    if (!metrics) return []
    let cumulative = 0
    return metrics.signups_by_day.map((p) => {
      cumulative += p.count
      return { date: p.date ?? '', signups: p.count, total: cumulative }
    })
  }, [metrics])

  const byMinutes = useMemo(() => {
    if (!metrics) return []
    const top = [...metrics.accounts]
      .sort((a, b) => b.analysis_minutes - a.analysis_minutes)
      .slice(0, TOP_N)
    return withUniqueLabels(top, (a) => a.email).map((a) => ({
      name: a.name,
      minutes: a.analysis_minutes,
    }))
  }, [metrics])

  const byStudies = useMemo(() => {
    if (!metrics) return []
    const top = [...metrics.accounts].sort((a, b) => b.studies_total - a.studies_total).slice(0, TOP_N)
    return withUniqueLabels(top, (a) => a.email).map((a) => ({
      name: a.name,
      active: a.studies_active,
      archived: a.studies_archived,
    }))
  }, [metrics])

  const byRespondents = useMemo(() => {
    if (!metrics) return []
    const top = [...metrics.studies].sort((a, b) => b.respondents - a.respondents).slice(0, TOP_N)
    return withUniqueLabels(top, (s) => `#${s.id}`).map((s) => ({
      name: s.name,
      respondents: s.respondents,
    }))
  }, [metrics])

  const studyStatus = useMemo(
    () =>
      metrics
        ? [
            { name: 'Active', value: metrics.totals.studies_active },
            { name: 'Archived', value: metrics.totals.studies_archived },
          ]
        : [],
    [metrics],
  )

  const axisProps = {
    tick: { fontSize: 11, fill: c.ink56 },
    stroke: c.ink32,
  }
  const tooltipStyle = {
    contentStyle: {
      borderRadius: 12,
      border: `1px solid ${c.ink08}`,
      fontSize: 12,
    },
  }

  return (
    <ResearcherShell crumbs={['Admin', 'Platform overview']} hideTopBarAction>
      <Stack spacing={3}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ color: c.ink }}>
              Platform overview
            </Typography>
            <Typography sx={{ fontSize: 14, color: c.ink56 }}>
              Global monitoring across all accounts, studies and analysis load.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<VisibilityRoundedIcon />}
            onClick={() => navigate('/admin/eye-tracking')}
            sx={{ textTransform: 'none', fontWeight: 700, flexShrink: 0 }}
          >
            Eye tracking lab
          </Button>
        </Box>

      {metrics === undefined ? (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      ) : metrics === null ? (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 400, gap: 1 }}>
          <Typography sx={{ fontWeight: 700, color: c.ink }}>
            Could not load admin metrics
          </Typography>
          <Typography sx={{ fontSize: 13, color: c.ink56 }}>
            You may not have admin access, or the server is unreachable.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={3}>

          {/* KPI stat cards */}
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
            }}
          >
            <StatCard
              label="Accounts"
              value={metrics.totals.accounts_total}
              sub={`${metrics.totals.researchers_total} researchers`}
              accent={c.coral}
            />
            <StatCard
              label="Studies"
              value={metrics.totals.studies_total}
              sub={`${metrics.totals.studies_active} active · ${metrics.totals.studies_archived} archived`}
              accent={c.navy600}
            />
            <StatCard
              label="Respondents"
              value={metrics.totals.respondents_total}
              sub={`${metrics.totals.respondents_completed} completed`}
              accent={c.success}
            />
            <StatCard
              label="Recordings"
              value={metrics.totals.recordings_total}
              accent={c.warning}
            />
            <StatCard
              label="Analysis minutes"
              value={metrics.totals.analysis_minutes_total.toLocaleString()}
              sub="total analyzed video"
              accent={theme.palette.error.main}
            />
            <StatCard
              label="Respondent-only"
              value={metrics.totals.respondent_accounts_total}
              sub="anonymous accounts"
              accent={c.ink48}
            />
          </Box>

          {/* Growth over time */}
          <ChartCard
            title="Researcher signups over time"
            subtitle="Cumulative researcher accounts and daily new signups"
          >
            <AreaChart data={growth} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="adminGrowth" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={c.coral} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={c.coral} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={c.ink08} />
              <XAxis dataKey="date" {...axisProps} />
              <YAxis allowDecimals={false} {...axisProps} />
              <Tooltip {...tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area
                type="monotone"
                dataKey="total"
                name="Total accounts"
                stroke={c.coral}
                strokeWidth={2}
                fill="url(#adminGrowth)"
              />
              <Area
                type="monotone"
                dataKey="signups"
                name="New that day"
                stroke={c.navy600}
                strokeWidth={1.5}
                fillOpacity={0}
              />
            </AreaChart>
          </ChartCard>

          {/* Two-column charts */}
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
            }}
          >
            <ChartCard
              title="Studies per account"
              subtitle={`Active vs archived — top ${TOP_N} by total`}
            >
              <BarChart data={byStudies} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.ink08} vertical={false} />
                <XAxis dataKey="name" {...axisProps} interval={0} angle={-25} textAnchor="end" height={70} />
                <YAxis allowDecimals={false} {...axisProps} />
                <Tooltip {...tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="active" name="Active" stackId="s" fill={c.success} radius={[0, 0, 0, 0]} />
                <Bar dataKey="archived" name="Archived" stackId="s" fill={c.ink32} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartCard>

            <ChartCard title="Study status" subtitle="All studies, platform-wide">
              <PieChart>
                <Pie
                  data={studyStatus}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={60}
                  outerRadius={95}
                  paddingAngle={2}
                >
                  <Cell fill={c.success} />
                  <Cell fill={c.ink32} />
                </Pie>
                <Tooltip {...tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ChartCard>
          </Box>

          {/* Analysis minutes per user */}
          <ChartCard
            title="Analysis minutes per account"
            subtitle={`Total analyzed video time (video × respondents) — top ${TOP_N}`}
          >
            <BarChart data={byMinutes} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={c.ink08} vertical={false} />
              <XAxis dataKey="name" {...axisProps} interval={0} angle={-25} textAnchor="end" height={70} />
              <YAxis {...axisProps} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="minutes" name="Minutes" fill={c.coral} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartCard>

          {/* Respondents per study */}
          <ChartCard
            title="Respondents per study"
            subtitle={`Top ${TOP_N} studies by respondent count`}
          >
            <BarChart data={byRespondents} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={c.ink08} vertical={false} />
              <XAxis dataKey="name" {...axisProps} interval={0} angle={-25} textAnchor="end" height={70} />
              <YAxis allowDecimals={false} {...axisProps} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="respondents" name="Respondents" fill={c.navy600} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartCard>

          {/* Analysis processing-time stats */}
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            }}
          >
            <StatCard
              label="Avg analysis time"
              value={`${metrics.analysis_time_stats.avg_seconds}s`}
              accent={c.coral}
            />
            <StatCard
              label="Median analysis time"
              value={`${metrics.analysis_time_stats.median_seconds}s`}
              accent={c.navy600}
            />
            <StatCard
              label="Slowest analysis"
              value={`${metrics.analysis_time_stats.max_seconds}s`}
              accent={theme.palette.error.main}
            />
            <StatCard
              label="Processing / video sec"
              value={`${metrics.analysis_time_stats.avg_ratio}×`}
              sub={`${metrics.analysis_time_stats.count} timed jobs`}
              accent={c.success}
            />
          </Box>

          {/* Video length vs analysis time scatter */}
          <ChartCard
            title="Video length vs. analysis time"
            subtitle={
              metrics.analysis_time_stats.truncated
                ? `Each point is one analysis — showing newest ${metrics.analysis_time_stats.shown} of ${metrics.analysis_time_stats.count}`
                : 'Each point is one analysis (succeeded jobs with recorded timing)'
            }
            height={360}
          >
            <ScatterChart margin={{ top: 8, right: 16, left: 4, bottom: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={c.ink08} />
              <XAxis
                type="number"
                dataKey="video_seconds"
                name="Video"
                unit="s"
                {...axisProps}
                label={{ value: 'Video length (s)', position: 'insideBottom', offset: -8, fontSize: 12, fill: c.ink56 }}
              />
              <YAxis
                type="number"
                dataKey="analysis_seconds"
                name="Analysis"
                unit="s"
                {...axisProps}
                label={{ value: 'Analysis time (s)', angle: -90, position: 'insideLeft', fontSize: 12, fill: c.ink56 }}
              />
              <ZAxis range={[45, 45]} />
              <Tooltip {...tooltipStyle} cursor={{ strokeDasharray: '3 3' }} />
              <Scatter data={metrics.analysis_points} fill={c.coral} fillOpacity={0.65} />
            </ScatterChart>
          </ChartCard>

          <Typography sx={{ fontSize: 12, color: c.ink48, textAlign: 'right' }}>
            <Chip
              size="small"
              label={`${metrics.accounts.length} accounts · ${metrics.studies.length} studies`}
              sx={{ bgcolor: c.ink04, color: c.ink56 }}
            />
          </Typography>
        </Stack>
      )}
      </Stack>
    </ResearcherShell>
  )
}
